import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Drawer, Row, Table, message, Tag } from "antd";
import React, { Fragment } from "react";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { RMCompanyActivity, RowSelection } from "../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import { RouteComponentProps } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import CompanyActivitySection from "../components/CompanyActivitySection";
import ExportData from "../components/ExportData";
import {
  injectKey,
  loadSavedFilters,
  request,
  responseHandler,
  getStateColor,
  formatSnakeCase,
  showTotalPagination,
} from "../helpers/utilities";
import { GET_CLIENT_ACTIVITIES } from "../gql/companies";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  companyActivities: RMCompanyActivity[];
  companyActivitiesCount: number;
  companyActivity: RMCompanyActivity | undefined;
  page: number;
  pageSize: number;
  q: string;
  state: string[];
  showCancelModal: boolean;
  showDrawer: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
  selectedIdsToExport: React.Key[];
}

interface Props extends RouteComponentProps<{}> {
  companyId?: string;
  isReadOnly?: boolean;
}

export function getColumns<T extends RMCompanyActivity = RMCompanyActivity>(
  toggleDrawer: (id: string | undefined) => void,
  companyId?: string,
): ColumnType<T>[] {
  const cols = [
    {
      title: "Service",
      dataIndex: ["service", "name"],
      key: "service",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return <span>{record?.service?.name}</span>;
      },
    },
    {
      title: "Category",
      dataIndex: ["category", "name"],
      key: "category",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return <span>{record?.category?.name}</span>;
      },
    },
    {
      title: "Sub Category",
      dataIndex: ["subCategory", "name"],
      key: "category",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return <span>{record?.subCategory?.name}</span>;
      },
    },
    {
      title: "Type",
      dataIndex: ["companyActivityType", "name"],
      key: "category",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return <span>{record?.companyActivityType?.name}</span>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return (
          <span>
            {record.date
              ? moment(record?.date, "YYYY-MM-DDTHH:MM:SSZ").format(
                "DD/MM/YYYY",
              )
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Assignee",
      dataIndex: ["assignee", "name"],
      key: "assignee",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return <span>{record?.assignee?.name}</span>;
      },
    },
    {
      title: "status",
      dataIndex: "status",
      key: "status",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        return (
         <Tag
            color={getStateColor(record.status)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(record.status)}
          </Tag>
        );
      },
    },
    {
      title: "Completed On",
      dataIndex: "completedOn",
      key: "completedOn",
      render(_: any, record: RMCompanyActivity): JSX.Element {
        //previous date being displayed
        return (
          <span>
            {record.completedOn
              ? moment(record?.completedOn, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")
              : "N/A"}
          </span>
        );
      },
    },
  ];

  return companyId
    ? cols
    : [
      {
        title: "Company",
        dataIndex: ["company", "name"],
        key: "company",
        render(_: any, record: RMCompanyActivity): JSX.Element {
          return <span>{record?.company?.name}</span>;
        },
      },
      ...cols,
    ];
}

export default class ClientActivityList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      companyActivities: [],
      companyActivitiesCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
      state: [],
      showCancelModal: false,
      showDrawer: false,
      companyActivity: undefined,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
      selectedIdsToExport: [],
    };
    this.nextPage = this.nextPage.bind(this);

    this.onSelectChange = this.onSelectChange.bind(this);
  }

  triggerImmediateSearch = async (): Promise<void> => {
    const { page, q } = this.state;
    interface Response {
      count: number;
      results: RMCompanyActivity[];
    }

    this.setState({ isLoading: true });

    let variables: any = { page, q };
    if (this.props.companyId) {
      variables["companyId"] = this.props.companyId;
    }

    try {
      const r: AxiosResponse = await request({
        query: GET_CLIENT_ACTIVITIES,
        variables,
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "companyActivities",
      );

      if (handled) {
        message.error("Failed To Load Company Activities");
      } else {
        this.setState({
          companyActivities: mutation.results,
          companyActivitiesCount: mutation.count,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState({ page, pageSize: pageSize }, async () => {
      if (this.state.selectedFieldsToDisplay.length < 1) {
        await this.triggerImmediateSearch();
      }
    });
  }

  async componentDidMount(): Promise<void> {
    if (loadSavedFilters("companyActivity").length < 1) {
      await this.triggerImmediateSearch();
    }
  }

  findActivityById = (id: string): RMCompanyActivity | undefined => {
    return this.state.companyActivities.find(
      (activity: RMCompanyActivity): boolean => activity.id === id,
    );
  };

  toggleModal = (currentActivityId: string | undefined): void => {
    this.setState(
      {
        companyActivity: currentActivityId
          ? this.findActivityById(currentActivityId)
          : undefined,
      },
      () => {
        this.setState({
          showDrawer: !this.state.showDrawer,
        });
      },
    );
  };

  getDrawerContent = (): JSX.Element => {
    const { companyActivity } = this.state;
    return (
      <>
        <CompanyActivitySection
          isEditing={companyActivity ? true : false}
          companyActivity={companyActivity}
          companyId={this.props.companyId ?? ""}
          closeDrawer={this.toggleModal}
          refreshList={this.triggerImmediateSearch}
        />
      </>
    );
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      companyActivities: data.results as RMCompanyActivity[],
      selectedFieldsToDisplay: fields,
      companyActivitiesCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      {
        removedField: field,
        selectedFieldsToDisplay: fields,
      },
      () => {
        localStorage.setItem(
          "companyActivity",
          JSON.stringify(this.state.selectedFieldsToDisplay),
        );
      },
    );

    if (fields.length === 0) this.triggerImmediateSearch();
  };

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  render(): JSX.Element | null {
    const {
      q,
      page,
      companyActivities,
      companyActivitiesCount,
      pageSize,
      selectedFieldsToDisplay,
      removedField,
      selectedIdsToExport,
    } = this.state;

    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };


    return (
      <>
        <EntitledSection entitlement={entitlements.CompanyActivities.list}>
          <AdvancedSearchModal
            toggleModal={this.toggleAdvancedSearchModel}
            showModal={this.state.showAdvancedSearchModal}
            model="companyActivity"
            onError={this.handleOnAdvancedSearchError}
            onData={this.handleOnAdvancedSearchData}
            triggerField={removedField?.name}
            pageSize={pageSize}
            page={page}
          />
          <Card>
              <Fragment>
                <PageToolbar
                  searchPlaceholder="Search by company name or petra id"
                  searchValue={q}
                  onSearchChange={this.handleSearchInput}
                  totalCount={companyActivitiesCount}
                  currentDataCount={companyActivities.length}
                  page={page}
                  pageSize={pageSize}
                  selectedCount={selectedIdsToExport.length}
                  modelName="activities"
                  advancedSearch={{
                    onOpen: this.toggleAdvancedSearchModel,
                    activeFields: selectedFieldsToDisplay,
                    onFieldRemove: this.handleOnFieldRemove,
                  }}
                  primaryAction={
                    !this.props.isReadOnly &&
                    this.props.companyId && (
                      <Button
                        className="base-button"
                        size="large"
                        data-testid="create-client-activity-button"
                        onClick={() => {
                          this.setState({ showDrawer: true });
                        }}
                      >
                        <PlusOutlined />
                        Company Activity
                      </Button>
                    )
                  }
                  actions={
                    <ExportData
                      model={"companyActivity"}
                      dataArray={companyActivities}
                      size={"large"}
                      customIds={selectedIdsToExport}
                    />
                  }
                />
                <Row>
                  <Col span={24}>
                    <Table
                      loading={this.state.isLoading}
                      scroll={{ x: 400 }}
                      columns={getColumns(
                        this.toggleModal,
                        this.props?.companyId,
                      )}
                      onRow={(record) =>
                        this.props.isReadOnly
                          ? {}
                          : {
                              onClick: () => {
                                this.toggleModal(record.id);
                              },
                              style: { cursor: "pointer" },
                            }
                      }
                      dataSource={injectKey<RMCompanyActivity>(
                        companyActivities,
                        "id",
                      )}
                      pagination={{
                        current: page,
                        defaultPageSize: pageSize,
                        total: companyActivitiesCount,
                        onChange: this.nextPage,
                        showTotal: showTotalPagination,
                      }}
                      rowSelection={rowSelection}
                    />
                  </Col>
                  {!this.props.isReadOnly && (
                    <Drawer
                      title="Edit Company Activity"
                      width={800}
                      onClose={() => this.toggleModal(undefined)}
                      visible={this.state.showDrawer}
                      bodyStyle={{ paddingBottom: 80 }}
                      maskClosable={false}
                    >
                      {this.getDrawerContent()}
                    </Drawer>
                  )}
                </Row>
              </Fragment>
            </Card>
        </EntitledSection>
      </>
    );
  }
}
